import { useState } from "react";
import { toast } from "sonner";
import { Gift, Printer } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Field } from "./Field";
import { openGiftCardPrint } from "./giftCardPrint";

const EMPTY = { amount: "", recipient_name: "", recipient_email: "", from_name: "Cake Society", message: "" };

function CompGiftForm({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!(Number(f.amount) > 0)) return toast.error("Enter a gift card amount.");
    if (!f.recipient_name.trim() || !f.recipient_email.trim() || !f.from_name.trim()) return toast.error("Fill in the recipient, their email and who it's from.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gifts/complimentary", { ...f, amount: Number(f.amount) });
      if (data.email_sent) toast.success(`Gift card ${data.code} emailed to ${data.recipient_email}`);
      else toast.warning(`Gift card ${data.code} created, but the email couldn't be sent — share the code yourself.`);
      setF(EMPTY); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  if (!open) return (
    <button data-testid="comp-gift-open" onClick={() => setOpen(true)} className="w-full p-4 bg-[#C6FF33] border-2 border-[#121212] text-left font-bold text-[#121212] inline-flex items-center gap-2"><Gift size={18} /> Issue complimentary gift card (free — no payment)</button>
  );
  return (
    <div data-testid="comp-gift-form" className="p-5 bg-white border-2 border-[#121212] space-y-3">
      <p className="font-serif font-black text-xl text-[#121212]">Complimentary gift card</p>
      <p className="text-xs text-stone-500">No payment is taken and it won't count as a sale. The recipient gets the gift card email right away and can use it on classes, camps, tutorials and the shop.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Amount ($)" type="number" data-testid="comp-gift-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <Field label="From (shown on the gift card)" data-testid="comp-gift-from" value={f.from_name} onChange={(e) => setF({ ...f, from_name: e.target.value })} />
        <Field label="Recipient name" data-testid="comp-gift-recipient-name" value={f.recipient_name} onChange={(e) => setF({ ...f, recipient_name: e.target.value })} />
        <Field label="Recipient email" type="email" data-testid="comp-gift-recipient-email" value={f.recipient_email} onChange={(e) => setF({ ...f, recipient_email: e.target.value })} />
      </div>
      <Field label="Personal message (optional)" type="textarea" data-testid="comp-gift-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
      <div className="flex gap-2">
        <button data-testid="comp-gift-submit" onClick={submit} disabled={busy} className="px-6 py-3 bg-[#121212] text-[#C6FF33] text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-40">{busy ? "Sending…" : "Issue & email gift card"}</button>
        <button data-testid="comp-gift-cancel" onClick={() => setOpen(false)} className="px-6 py-3 border-2 border-[#121212] text-xs tracking-[0.2em] uppercase font-bold">Cancel</button>
      </div>
    </div>
  );
}

export default function AdminGifts({ ctx }) {
  const { gifts, refundGift, reload } = ctx;
  const [sel, setSel] = useState([]);
  const printable = (g) => g.status === "paid";
  const print = (list) => { if (!openGiftCardPrint(list)) toast.error("Please allow pop-ups to print gift cards."); };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="mt-10 space-y-2">
      <div className="mb-4"><CompGiftForm onDone={() => reload("gifts")} /></div>
      {sel.length > 0 && (
        <div data-testid="gift-print-bar" className="sticky top-2 z-10 p-3 bg-[#121212] text-white flex items-center justify-between gap-3">
          <span className="text-sm">{sel.length} gift card{sel.length > 1 ? "s" : ""} selected · prints 2 per sheet</span>
          <div className="flex gap-2">
            <button data-testid="gift-print-selected" onClick={() => print(gifts.filter((g) => sel.includes(g.id)))} className="px-4 py-2 bg-[#C6FF33] text-[#121212] text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1"><Printer size={14} /> Print selected</button>
            <button data-testid="gift-print-clear" onClick={() => setSel([])} className="px-3 py-2 border border-white/40 text-xs uppercase tracking-wider">Clear</button>
          </div>
        </div>
      )}
      {gifts.length === 0 ? <p className="text-[#121212]/60">No gifts yet.</p> :
        gifts.map((g) => (
          <div key={g.id} data-testid={`gift-row-${g.id}`} className="p-4 bg-white border-2 border-[#121212] flex justify-between gap-4 flex-wrap">
            <div className="flex gap-3">
              {printable(g) && <input type="checkbox" data-testid={`gift-select-${g.id}`} checked={sel.includes(g.id)} onChange={() => toggle(g.id)} className="mt-1 w-4 h-4 accent-[#121212]" />}
              <div>
              <p className="font-mono font-bold">{g.code} {g.complimentary && <span data-testid={`gift-comp-badge-${g.id}`} className="ml-2 align-middle font-sans text-[10px] px-2 py-0.5 bg-[#C6FF33] border border-[#121212] uppercase tracking-wider">Complimentary</span>}</p>
              <p className="text-sm">${g.amount} · Balance ${g.balance} · For {g.recipient_name} ({g.recipient_email})</p>
              <p className="text-xs text-[#121212]/60">From {g.buyer_name} · {g.status}</p>
              </div>
            </div>
            <div className="flex gap-2 items-start">
            {printable(g) && (
              <button data-testid={`gift-print-${g.id}`} onClick={() => print([g])} className="px-4 py-2 border-2 border-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#C6FF33] inline-flex items-center gap-1"><Printer size={14} /> Print</button>
            )}
            {g.status === "paid" && g.balance > 0 && (
              <button data-testid={`refund-${g.id}`} onClick={() => refundGift(g.id)} className="px-4 py-2 border-2 border-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#FF8F80]">{g.complimentary ? "Void" : "Refund"}</button>
            )}
            </div>
          </div>
        ))}
    </div>
  );
}
